import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tab,
  Paper,
  Menu,
  MenuItem,
} from '@mui/material';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { getAllGroups, getAllUsers } from '../graphql/queries';
import { API } from '../services/amplifyCompat';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { SortLeaderboard } from './SortLeaderboard';
import { useLanguageContext } from '../components/contexts/LanguageContext';

import useTranslation from './/customHooks/translations';

export const tabKeys = ['globalGroups', 'globalUsers'];

const StyledTableBody = styled(TableBody)`
  td {
    color: #0F172A;
    font-weight: 500;
    font-size: 0.9rem;
  }
  .MuiTableCell-root {
    background: transparent;
  }
  .currentGroupOrUser {
    background: linear-gradient(91.49deg, #F0FDFA 0.29%, #CCFBF1 100%);
    th,td {
      color: #0F766E;
      font-weight: 700;
    }
    a {
      color: #0F766E;
    }
  }
`;

const GlobalLeaderboard = ({isAdminPanel = false}) => {
  const navigate = useNavigate();
  const translation = useTranslation();
  const filters = useMemo(
    () => [
    { name: translation.totalCO2Saved, property: 'total_co2' },
    { name: translation.totalPoints, property: 'total_points' },
    ],
    [
      translation.totalCO2Saved,
      translation.totalPoints,
    ]
  );
  const [groups, setGroups] = useState();
  const [users, setUsers] = useState();
  const [selectedTab, setSelectedTab] = useState(tabKeys[1]);
  const [selectedFilter, setSelectedFilter] = useState(filters[1]);
  const [filteredGroups, setFilteredGroups] = useState();
  const [filteredUsers, setFilteredUsers] = useState();
  const [openFilterMenu, setOpenFilterMenu] = useState(false);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const { language } = useLanguageContext();
  // avoid a layout jump in the table when reaching the last page with empty rows
  let emptyRows =
    page <= 0
      ? 0
      : selectedTab === tabKeys[1]
        ? Math.max(0, (1 + page) * rowsPerPage - groups.length)
      : selectedTab === tabKeys[0]
          ? Math.max(0, (1 + page) * rowsPerPage - users.length)
          : 0;

  useEffect(() => {
    const abortController = new AbortController();

    const getTableData = async () => {
      try {
        const [userRes, groupRes] = await Promise.all([
          API.graphql({
            query: getAllUsers,
            signal: abortController.signal,
            suppressTransientNetworkErrorCapture: true,
          }),
          API.graphql({
            query: getAllGroups,
            signal: abortController.signal,
            suppressTransientNetworkErrorCapture: true,
          }),
        ]);

        if (!abortController.signal.aborted) {
          setUsers(userRes.data.getAllUsers);
          setGroups(groupRes.data.getAllGroups);
          setSelectedTab(tabKeys[1]);
        }
      } catch (error) {
        if (error?.name === 'AbortError' || abortController.signal.aborted) {
          return;
        }

        setUsers([]);
        setGroups([]);
      }
    };

    getTableData();
    setSelectedTab(tabKeys[1]);
    setSelectedFilter(filters[1]);

    return () => abortController.abort();
  }, [filters, language]);

  //filters groups when group state is initially set, filters groups and group members every time a new filter is selected
  useEffect(() => {
    if (groups && users) {
      handleFilterSelection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, users, selectedFilter, selectedTab]);

  //sets filter back to default (total co2) and sets page back to first page on tab change
  const handleTabChange = (e, newValue) => {
    setSelectedTab(newValue);
    setSelectedFilter(filters[1]);
    setPage(0);
  };

  /** functions for filter menu */
  const handleClick = (e) => {
    setFilterMenuAnchor(e.currentTarget);
    setOpenFilterMenu(true);
  };

  /** functions for formatNumber */
  function formatNumber(num) {
    if (num == null || isNaN(num)) return '0 g';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(0) + ' tonnes';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + ' kg';
    } else {
      return num.toFixed(0) + ' g';
    }
  }

  function formatPoints(num) {
    if (num == null || isNaN(num)) return '0';
    if (num >= 1000000) {
      return (num / 1000000).toFixed(0) + ' M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(0) + ' K';
    } else {
      return num.toLocaleString();
    }
  }

  const handleClose = () => {
    setFilterMenuAnchor(null);
    setOpenFilterMenu(false);
  };

  const handleFilterSelection = () => {
    const propertySelected = selectedFilter.property;
    //if Global Group tab is selected, apply the selected filter to all groups
    if (selectedTab === tabKeys[0] && groups) {
      //make a mutable copy of the groups array, sort that array and update the in state version of groups
      let groupsArrayCopy = [...groups];
      let sortedByFilter = groupsArrayCopy.sort(
        (a, b) => b[propertySelected] - a[propertySelected]
      );
      setFilteredGroups(sortedByFilter);
    }
    //if Group Members tab is selected, apply the selected filter to all users
    if (selectedTab === tabKeys[1] && users) {
      //make a mutable copy of the users array, sort that array and update the in state version of users
      let usersArrayCopy = [...users];
      let sortedByFilter = usersArrayCopy.sort(
        (a, b) => b[propertySelected] - a[propertySelected]
      );
      setFilteredUsers(sortedByFilter);
    }
    handleClose();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  /** function for displaying the leaderboard tables */

  const renderTable = () => {
    return (
      <>
        <TableContainer component={Paper} sx={{ mt: '1em', backgroundColor: 'transparent', backgroundImage: 'none', boxShadow: 'none', border: 'none' }}>
          <Table stickyHeader aria-label="group leaderboard">
            <caption>
              {translation.formatString(translation.leaderboardDisplaying, {
                tab: translation[selectedTab],
              })}
              {selectedFilter.name}
            </caption>
            <TableHead sx={{ mt: '1em', backgroundColor: 'transparent', borderRadius: '4px 4px 0 0' }}>
              <TableRow sx={{ position: 'relative', zIndex: '0', fontWeight: 'bold' }}>
                <TableCell>{translation.rank}</TableCell>
                <TableCell align="left">{translation.name}</TableCell>
                {isAdminPanel && selectedTab === tabKeys[0] && <TableCell align="right">{translation.membersCount}</TableCell>}
                <TableCell align="right">{translation.totalCO2}</TableCell>
                <TableCell align="right">{translation.totalPoints}</TableCell>
              </TableRow>
            </TableHead>
            <StyledTableBody>
              {/* if Global Groups tab is selected, display all groups data in table body*/}
              {selectedTab === tabKeys[0] &&
                filteredGroups &&
                (rowsPerPage > 0
                  ? filteredGroups.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  : filteredGroups
                ).map((group, index) => (
                  <TableRow
                    key={group.group_id}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                    }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ color: '#0D9488', fontWeight: 700, fontFamily: '"Outfit", "DM Sans", sans-serif' }}
                    >
                      {page > 0 ? rowsPerPage * page + index + 1 : index + 1}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <Link
                        onClick={() => {
                          navigate(`/group-profile/${group.group_name}`);
                        }}
                        sx={{
                          color: '#0F172A',
                          fontSize: '0.95em',
                          fontWeight: 500,
                          textDecorationColor: '#0D9488',
                          ':hover': { color: '#0D9488', cursor: 'pointer' },
                        }}
                      >
                        {group.group_name}
                      </Link>
                    </TableCell>
                    {isAdminPanel && <TableCell align="right">{formatPoints(group.members_count)}</TableCell>}
                    <TableCell align="right">{formatNumber(group.total_co2)}</TableCell>
                    <TableCell align="right">{formatPoints(group.total_points)}</TableCell>
                  </TableRow>
                ))}

              {/* if Global Users tab is selected, display all user data in table body*/}
              {selectedTab === tabKeys[1] &&
                filteredUsers &&
                (rowsPerPage > 0
                  ? filteredUsers.slice(
                    page * rowsPerPage,
                    page * rowsPerPage + rowsPerPage
                  )
                  : filteredUsers
                ).map((user, index) => (
                  <TableRow
                    key={user.user_id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{ color: '#0D9488', fontWeight: 700, fontFamily: '"Outfit", "DM Sans", sans-serif' }}
                    >
                      {page > 0 ? rowsPerPage * page + index + 1 : index + 1}
                    </TableCell>
                    <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                      {user.name}
                    </TableCell>
                    <TableCell align="right">
                      {formatNumber(user.total_co2)}
                    </TableCell>
                    <TableCell align="right">{formatPoints(user.total_points)}</TableCell>
                  </TableRow>
                ))}
              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </StyledTableBody>
          </Table>
        </TableContainer>
        {/* render the correct pagination options for each table */}
        {selectedTab === tabKeys[0] && groups && (
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={groups && groups.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            showFirstButton
            showLastButton
            labelRowsPerPage={translation.labelRowsPerPage}
            labelDisplayedRows={
              ({ from, to, count }) => {
                return '' + from + ' - ' + to + ' ' + translation.of + ' ' + count
              }
            }
          />
        )}
        {selectedTab === tabKeys[1] && users && (
          <Box component="div" sx={{marginTop: 'auto'}}>


            <TablePagination
              rowsPerPageOptions={[5, 10]}
              component="div"
              count={users.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={translation.labelRowsPerPage}
              labelDisplayedRows={({ from, to, count }) => {
                return `${from} - ${to} ${translation.of} ${count}`;
              }}
              onPageChange={() => {}}
              showFirstButton={false}
              showLastButton={false}
              ActionsComponent={() => null}
            />
          </Box>
        )}
      </>
    );
  };

  return (
    <>
      {groups && (
        <>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: { xs: 'center', sm: 'space-between' },
              alignItems: 'center',
              gap: { xs: '1em', sm: '0' },
              mb: '1em',
              width: '100%',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
                <SortLeaderboard
                  onSelectSort={handleClick}
                  selectedSort={selectedFilter}
                />
                <Menu
                  id="filter-menu"
                  open={openFilterMenu}
                  anchorEl={filterMenuAnchor}
                  onClose={handleClose}
                >
                  {filters.map((filter, index) => (
                    <MenuItem
                      key={index}
                      onClick={() => setSelectedFilter(filter)}
                    >
                      {filter.name}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
              <TablePagination
                rowsPerPageOptions={[]}
                component="div"
                count={users.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                showFirstButton
                showLastButton
                labelRowsPerPage=""
                labelDisplayedRows={() => ""}
                onRowsPerPageChange={() => {}}
                SelectProps={{ native: true }}
              />
              </Box>
            </Box>

          </Box>



          <TabContext
            value={
              tabKeys.indexOf(selectedTab) !== -1 ? selectedTab : tabKeys[0]
            }
          >
            <Box
              sx={{
                borderTop: 1,
                borderColor: 'divider',
                width: '100%',
                display: { xs: 'contents', sm: 'flex' },
                flexDirection: 'column',
                flexGrow: 1,
              }}

            >
              <TabList
                value={tabKeys.indexOf(selectedTab) !== -1 ? selectedTab : tabKeys[1]}
                onChange={handleTabChange}
                aria-label="Leaderboard tabs"
                sx={{
                  width: { xs: '100%' },
                  margin: { xs: '0 auto -16px' },
                  borderBottom: { xs: 1, sm: 1 },
                  borderTopColor: { xs: 'divider', sm: 'transparent' },
                  borderBottomColor: { xs: 'divider', sm: 'transparent' },
                }}
              >
                <Tab label={translation[tabKeys[1]]} value={tabKeys[1]} />
                <Tab label={translation[tabKeys[0]]} value={tabKeys[0]} />
              </TabList>
              <TabPanel
                value={tabKeys[0]}
                sx={{ width: '100%', padding: 0, display: 'flex', flexDirection: 'column' }}
              >
                {renderTable()}
              </TabPanel>
              <TabPanel
                value={tabKeys[1]}
                sx={{ width: '100%', padding: 0, display: 'flex', flexDirection: 'column' }}
              >
                {renderTable()}
              </TabPanel>
            </Box>
          </TabContext>
        </>
      )}
    </>
  );
};

export default GlobalLeaderboard;
